import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, MoreVertical, Users, MessageSquare,
  Calendar, Plus, Settings2, ChevronDown, Eye,
  Search, SlidersHorizontal, X, Menu, Filter, Upload, 
  Trash2, ExternalLink, Circle, Check
} from 'lucide-react';
import { read, utils } from 'xlsx';

const TrackerDetail = () => {
  const { id } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  
  // Define status and priority options first
  const statusOptions = [
    { id: 'completed', label: 'Completed', color: '#00C875' },
    { id: 'in-progress', label: 'In Progress', color: '#FDAB3D' },
    { id: 'stuck', label: 'Stuck', color: '#E2445C' },
    { id: 'not-started', label: 'Not Started', color: '#C4C4C4' }
  ];

  const priorityOptions = [
    { id: 'high', label: 'High', color: '#E2445C' },
    { id: 'medium', label: 'Medium', color: '#FDAB3D' },
    { id: 'low', label: 'Low', color: '#00C875' }
  ];

  // Initialize customStatuses and customTags before using them
  const [customStatuses, setCustomStatuses] = useState(statusOptions);
  const [customTags, setCustomTags] = useState([
    { id: 'urgent', label: 'Urgent', color: '#E2445C' },
    { id: 'backend', label: 'Backend', color: '#579BFC' },
    { id: 'frontend', label: 'Frontend', color: '#A25DDC' },
  ]);

  // Then initialize newItem state using customStatuses
  const [newItem, setNewItem] = useState({
    name: '',
    status: customStatuses[0],
    priority: priorityOptions[0],
    tags: [],
    assignee: null,
    dueDate: '',
    test: '#FFFFFF',
  });

  // Rest of your state declarations
  const [columns, setColumns] = useState([
    { id: 'name', type: 'text', title: 'Item', width: '250px', show: true },
    { id: 'status', type: 'status', title: 'Status', width: '140px', show: true },
    { id: 'priority', type: 'priority', title: 'Priority', width: '120px', show: true },
    { id: 'assignee', type: 'person', title: 'Owner', width: '140px', show: true },
    { id: 'dueDate', type: 'date', title: 'Due Date', width: '120px', show: true },
    { id: 'lastUpdated', type: 'date', title: 'Last Updated', width: '120px', show: true },
  ]);

  const [items, setItems] = useState([
    {
      id: 1,
      name: 'DC-01 Singapore',
      status: customStatuses[0],
      priority: priorityOptions[0],
      assignee: { id: 1, name: 'John Doe', avatar: 'JD' },
      dueDate: '2024-04-01',
      lastUpdated: '2024-03-19T10:30:00',
      comments: []
    },
    // Add more items...
  ]);

  const [showNewItemModal, setShowNewItemModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const buttonRefs = useRef({});

  const addNewItem = () => {
    setItems(prev => [...prev, {
      id: Date.now(),
      ...newItem,
      comments: [],
      lastUpdated: new Date().toISOString()
    }]);
    setShowNewItemModal(false);
    setNewItem({
      name: '',
      status: customStatuses[0],
      priority: priorityOptions[0],
      tags: [],
      assignee: null,
      dueDate: '',
      test: '#FFFFFF',
    });
  };

  const addCustomTag = (label, color) => {
    const newTag = {
      id: label.toLowerCase().replace(/\s+/g, '-'),
      label,
      color
    };
    setCustomTags(prev => [...prev, newTag]);
  };

  const addCustomStatus = (label, color) => {
    const newStatus = {
      id: label.toLowerCase().replace(/\s+/g, '-'),
      label,
      color
    };
    setCustomStatuses(prev => [...prev, newStatus]);
  };

  const updateItemValue = (itemId, columnId, value) => {
    console.log('Before update:', items); // Debug log
    setItems(prevItems => {
      const newItems = prevItems.map(item =>
        item.id === itemId
          ? { ...item, [columnId]: value }
          : item
      );
      console.log('After update:', newItems); // Debug log
      return newItems;
    });
  };

  const TableHeader = () => (
    <div className="sticky top-0 z-[5] bg-white/95 backdrop-blur-sm border-b border-gray-200">
      <div className="flex items-center h-12 text-xs font-medium text-gray-500">
        <div className="w-[40px] flex-shrink-0 px-2 flex items-center justify-center">
          <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
        </div>
        {columns.filter(col => col.show).map(column => (
          <div
            key={column.id}
            className="flex items-center px-4 border-r border-gray-100 group/header"
            style={{ width: column.width }}
          >
            <span className={`truncate ${column.id === 'name' ? 'text-left' : 'text-center w-full'}`}>
              {column.title}
            </span>
            <button className="ml-1 opacity-0 group-hover/header:opacity-100 transition-opacity">
              <Menu className="w-3 h-3" />
            </button>
          </div>
        ))}
        <div className="flex-grow flex justify-end px-4">
          <button
            onClick={() => setShowColumnMenu(true)}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );

  const TableRow = ({ item }) => (
    <div className="group/row flex items-center h-[52px] hover:bg-gray-50/80 transition-colors relative">
      <div className="absolute inset-x-0 h-[1px] top-0 bg-gray-100"></div>
      <div className="w-[40px] flex-shrink-0 px-2 flex items-center justify-center">
        <input 
          type="checkbox" 
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500
            opacity-0 group-hover/row:opacity-100 transition-opacity" 
        />
      </div>
      {columns.filter(col => col.show).map(column => (
        <div
          key={column.id}
          className={`flex items-center px-4 border-r border-gray-50 ${
            column.id === 'name' ? 'hover:bg-gray-100/50' : ''
          }`}
          style={{ width: column.width }}
        >
          <div className={`w-full ${column.id === 'name' ? 'text-left' : 'text-center'}`}>
            {renderCell(column, item)}
          </div>
        </div>
      ))}
    </div>
  );

  const renderCell = (column, item) => {
    switch (column.type) {
      case 'text':
        return (
          <div className="flex items-center space-x-2 py-2">
            <span className="text-sm font-medium text-gray-900 truncate">{item[column.id]}</span>
            {item.comments?.length > 0 && (
              <div className="flex items-center text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                <MessageSquare className="w-3 h-3 mr-1" />
                {item.comments.length}
              </div>
            )}
          </div>
        );

      case 'status':
      case 'priority':
        const options = column.type === 'status' ? customStatuses : priorityOptions;
        const value = item[column.id];
        const dropdownId = `${item.id}-${column.id}`;
        
        return (
          <div className="relative dropdown-container" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              ref={el => buttonRefs.current[dropdownId] = el}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
              }}
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium 
                transition-all hover:opacity-90 min-w-[90px] justify-center"
              style={{
                backgroundColor: value?.color || '#E5E7EB',
                color: value?.color ? '#FFFFFF' : '#6B7280'
              }}
            >
              <span>{value?.label || 'Not set'}</span>
            </button>
            {activeDropdown === dropdownId && (
              <div 
                className="fixed z-[100]"
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: '200px'
                }}
              >
                <div
                  className="bg-white rounded-lg shadow-lg border border-gray-200 py-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {options.map(option => (
                    <button
                      key={option.id}
                      type="button"
                      className="w-full px-3 py-1.5 text-left hover:bg-gray-50 text-xs flex items-center space-x-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        updateItemValue(item.id, column.id, option);
                        setActiveDropdown(null);
                      }}
                    >
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: option.color }}
                      />
                      <span className="truncate">{option.label}</span>
                      {value?.id === option.id && (
                        <Check className="w-3 h-3 ml-auto text-blue-500" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'person':
        const person = item[column.id];
        return person ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center
              text-xs font-medium text-blue-700 ring-2 ring-white">
              {person.avatar}
            </div>
            <span className="text-sm text-gray-700">{person.name}</span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">Unassigned</span>
        );

      case 'date':
        return item[column.id] ? (
          <span className="text-sm text-gray-600 font-medium">
            {new Date(item[column.id]).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        );

      case 'tags':
        const tags = item[column.id] || [];
        const tagDropdownId = `${item.id}-${column.id}`;
        
        return (
          <div className="relative dropdown-container" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap gap-1">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ 
                    backgroundColor: tag.color,
                    color: '#FFFFFF'
                  }}
                >
                  {tag.label}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const newTags = tags.filter((_, i) => i !== index);
                      updateItemValue(item.id, column.id, newTags);
                    }}
                    className="ml-1 hover:opacity-80 text-white/80 hover:text-white"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <button
                ref={el => buttonRefs.current[tagDropdownId] = el}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setActiveDropdown(activeDropdown === tagDropdownId ? null : tagDropdownId);
                }}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                  bg-gray-100 text-gray-600 hover:bg-gray-200"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
            {activeDropdown === tagDropdownId && (
              <div 
                className="fixed z-[100]"
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: '200px'
                }}
              >
                <div
                  className="bg-white rounded-lg shadow-lg border border-gray-200 py-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {customTags.map(tag => (
                    <button
                      key={tag.id}
                      type="button"
                      className="w-full px-3 py-1.5 text-left hover:bg-gray-50 text-xs flex items-center space-x-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const newTags = [...tags, tag];
                        updateItemValue(item.id, column.id, newTags);
                        setActiveDropdown(null);
                      }}
                    >
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="truncate">{tag.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'icon':
        const icon = item[column.id];
        return (
          <div className="relative group/icon">
            <div className="cursor-pointer p-1 hover:bg-gray-50 rounded">
              {icon?.icon || <Plus className="w-4 h-4 text-gray-400" />}
            </div>
            <div className="absolute top-full left-0 mt-1 hidden group-hover/icon:block z-[20]">
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-2 w-[200px]">
                <div className="grid grid-cols-6 gap-1">
                  {column.options?.map(option => (
                    <button
                      key={option.id}
                      className="p-2 hover:bg-gray-50 rounded"
                      onClick={() => updateItemValue(item.id, column.id, option)}
                    >
                      {option.icon}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'color':
        const colorValue = item[column.id] || '#FFFFFF';
        const colorDropdownId = `${item.id}-${column.id}`;
        
        return (
          <div className="relative dropdown-container" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              ref={el => buttonRefs.current[colorDropdownId] = el}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveDropdown(activeDropdown === colorDropdownId ? null : colorDropdownId);
              }}
              className="flex items-center space-x-2 px-2 py-1 rounded hover:bg-gray-50"
            >
              <div
                className="w-6 h-6 rounded border border-gray-200"
                style={{ backgroundColor: colorValue }}
              />
              <span className="text-xs text-gray-600">{colorValue}</span>
            </button>
            {activeDropdown === colorDropdownId && (
              <div 
                className="fixed z-[100]"
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: '200px'
                }}
              >
                <div
                  className="bg-white rounded-lg shadow-lg border border-gray-200 p-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="space-y-2">
                    <input
                      type="color"
                      value={colorValue}
                      onChange={(e) => {
                        updateItemValue(item.id, column.id, e.target.value);
                        setActiveDropdown(null);
                      }}
                      className="w-full h-8 rounded cursor-pointer"
                    />
                    <div className="flex flex-wrap gap-1">
                      {['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD', 
                        '#D4A5A5', '#9B59B6', '#3498DB', '#E67E22', '#2ECC71']
                        .map(color => (
                          <button
                            key={color}
                            onClick={() => {
                              updateItemValue(item.id, column.id, color);
                              setActiveDropdown(null);
                            }}
                            className="w-6 h-6 rounded border border-gray-200 hover:scale-110 transition-transform"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <span className="text-sm text-gray-600">
            {item[column.id]?.toString() || ''}
          </span>
        );
    }
  };

  const NewItemModal = () => {
    const users = [
      { id: 1, name: 'John Doe', avatar: 'JD' },
      { id: 2, name: 'Jane Smith', avatar: 'JS' },
    ];

    return (
      <>
        <div 
          className="fixed inset-0 bg-black/20 z-[49]" 
          onClick={() => setShowNewItemModal(false)}
        />
        <div className="fixed inset-0 flex items-start justify-center pt-20 z-[50]">
          <div 
            role="dialog" 
            className="bg-white rounded-lg shadow-xl w-96"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">New Item</h3>
                <button
                  onClick={() => setShowNewItemModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newItem.name}
                  onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                    focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={newItem.status.id}
                  onChange={(e) => {
                    const status = customStatuses.find(s => s.id === e.target.value);
                    setNewItem(prev => ({ ...prev, status }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  {customStatuses.map(status => (
                    <option key={status.id} value={status.id}>{status.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={newItem.priority.id}
                  onChange={(e) => {
                    const priority = priorityOptions.find(p => p.id === e.target.value);
                    setNewItem(prev => ({ ...prev, priority }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  {priorityOptions.map(priority => (
                    <option key={priority.id} value={priority.id}>{priority.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
                <select
                  value={newItem.assignee?.id || ''}
                  onChange={(e) => {
                    const selectedUser = users.find(u => u.id === Number(e.target.value));
                    setNewItem(prev => ({ ...prev, assignee: selectedUser || null }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="">Unassigned</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={newItem.dueDate}
                  onChange={(e) => setNewItem(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowNewItemModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={addNewItem}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  const ImportModal = () => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState([]);
    const [error, setError] = useState(null);
    const [mapping, setMapping] = useState({
      name: '',
      status: '',
      priority: '',
      dueDate: '',
      assignee: ''
    });
    const fileInputRef = useRef(null);

    const handleFileUpload = async (event) => {
      const file = event.target.files[0];
      if (file) {
        try {
          const data = await readExcelFile(file);
          if (data && data.length > 0) {
            setFile(file);
            setPreview(data.slice(0, 5)); // Show first 5 rows as preview
            setError(null);
            
            // Auto-map columns based on headers
            const headers = Object.keys(data[0]);
            const newMapping = {
              name: headers.find(h => /name|item|title/i.test(h)) || '',
              status: headers.find(h => /status|state/i.test(h)) || '',
              priority: headers.find(h => /priority|importance/i.test(h)) || '',
              dueDate: headers.find(h => /due|deadline|date/i.test(h)) || '',
              assignee: headers.find(h => /assignee|owner|responsible/i.test(h)) || ''
            };
            setMapping(newMapping);
          }
        } catch (err) {
          setError('Error reading file. Please make sure it\'s a valid Excel file.');
          setFile(null);
          setPreview([]);
        }
      }
    };

    const readExcelFile = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = e.target.result;
            const workbook = read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = utils.sheet_to_json(firstSheet);
            resolve(jsonData);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(file);
      });
    };

    const handleImport = async () => {
      if (!file || !preview.length) return;

      try {
        const data = await readExcelFile(file);
        const newItems = data.map(row => ({
          id: Date.now() + Math.random(),
          name: row[mapping.name] || 'Untitled',
          status: findMatchingStatus(row[mapping.status]),
          priority: findMatchingPriority(row[mapping.priority]),
          assignee: { name: row[mapping.assignee] || 'Unassigned', avatar: 'UA' },
          dueDate: row[mapping.dueDate] || new Date().toISOString().split('T')[0],
          lastUpdated: new Date().toISOString(),
          comments: []
        }));

        setItems(prev => [...prev, ...newItems]);
        setShowImportModal(false);
      } catch (error) {
        setError('Error importing data. Please check your file format.');
      }
    };

    const findMatchingStatus = (statusText) => {
      if (!statusText) return statusOptions[0];
      const normalized = statusText.toLowerCase().trim();
      return statusOptions.find(s => 
        s.label.toLowerCase().includes(normalized) || 
        normalized.includes(s.label.toLowerCase())
      ) || statusOptions[0];
    };

    const findMatchingPriority = (priorityText) => {
      if (!priorityText) return priorityOptions[0];
      const normalized = priorityText.toLowerCase().trim();
      return priorityOptions.find(p => 
        p.label.toLowerCase().includes(normalized) || 
        normalized.includes(p.label.toLowerCase())
      ) || priorityOptions[0];
    };

    return (
      <div className="fixed inset-0 bg-black/20 flex items-start justify-center pt-20 z-[50]">
        <div role="dialog" className="bg-white rounded-lg shadow-xl w-[800px] max-h-[80vh] overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Import Items</h3>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-4 space-y-4">
            {/* File Upload */}
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 
                  bg-blue-50 rounded-lg hover:bg-blue-100"
              >
                <Upload className="w-4 h-4 mr-2" />
                Choose Excel File
              </button>
              <p className="mt-2 text-sm text-gray-500">
                Supported formats: .xlsx, .xls, .csv
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Column Mapping */}
            {preview.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Map Columns</h4>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(mapping).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </label>
                      <select
                        value={value}
                        onChange={(e) => setMapping(prev => ({ ...prev, [key]: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                          focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      >
                        <option value="">-- Select Column --</option>
                        {preview[0] && Object.keys(preview[0]).map(header => (
                          <option key={header} value={header}>{header}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                {/* Preview */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Preview</h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {Object.keys(mapping).map(key => (
                            <th
                              key={key}
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {preview.map((row, idx) => (
                          <tr key={idx}>
                            {Object.values(mapping).map((col, colIdx) => (
                              <td
                                key={colIdx}
                                className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap"
                              >
                                {row[col]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={() => setShowImportModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white 
                border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={!file || !preview.length}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 
                rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Import Items
            </button>
          </div>
        </div>
      </div>
    );
  };

  const SettingsModal = () => {
    const [activeTab, setActiveTab] = useState('columns');
    const [newColumnData, setNewColumnData] = useState({
      title: '',
      type: 'text',
      options: []
    });
    const [newOption, setNewOption] = useState({
      label: '',
      color: '#3B82F6',
      icon: 'Circle'
    });
    const [newStatus, setNewStatus] = useState({ label: '', color: '#00C875' });
    const [newTag, setNewTag] = useState({ label: '', color: '#579BFC' });

    const columnTypes = [
      { id: 'text', label: 'Text' },
      { id: 'status', label: 'Status with Colors' },
      { id: 'priority', label: 'Priority with Colors' },
      { id: 'date', label: 'Date' },
      { id: 'person', label: 'Person' },
      { id: 'tags', label: 'Tags' },
      { id: 'number', label: 'Number' },
      { id: 'checkbox', label: 'Checkbox' },
      { id: 'color', label: 'Color' },
      { id: 'icon', label: 'Icon' },
      { id: 'link', label: 'Link' }
    ];

    const iconOptions = [
      { id: 'circle', label: 'Circle', icon: '⭕' },
      { id: 'star', label: 'Star', icon: '⭐' },
      { id: 'heart', label: 'Heart', icon: '❤️' },
      { id: 'check', label: 'Check', icon: '✅' },
      { id: 'warning', label: 'Warning', icon: '⚠️' },
      { id: 'flag', label: 'Flag', icon: '🚩' }
    ];

    const addNewColumn = () => {
      if (newColumnData.title.trim()) {
        const newColumn = {
          id: newColumnData.title.toLowerCase().replace(/\s+/g, '-'),
          ...newColumnData,
          width: '140px',
          show: true,
          options: newColumnData.type === 'icon' 
            ? iconOptions 
            : newColumnData.type === 'status' || newColumnData.type === 'tags'
              ? newColumnData.options 
              : []
        };
        setColumns(prev => [...prev, newColumn]);
        setNewColumnData({ title: '', type: 'text', options: [] });
      }
    };

    const addOptionToColumn = () => {
      if (newOption.label.trim()) {
        setNewColumnData(prev => ({
          ...prev,
          options: [...prev.options, {
            id: newOption.label.toLowerCase().replace(/\s+/g, '-'),
            ...newOption
          }]
        }));
        setNewOption({ label: '', color: '#3B82F6', icon: 'Circle' });
      }
    };

    return (
      <div className="fixed inset-0 bg-black/20 flex items-start justify-center pt-20 z-[50]">
        <div role="dialog" className="bg-white rounded-lg shadow-xl w-[900px] max-h-[80vh] overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Tracker Settings</h3>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex h-[calc(80vh-64px)]">
            {/* Sidebar */}
            <div className="w-48 border-r border-gray-200 p-4 space-y-1">
              <button
                onClick={() => setActiveTab('columns')}
                className={`w-full px-3 py-2 text-sm font-medium rounded-lg text-left
                  ${activeTab === 'columns' 
                    ? 'bg-blue-50 text-blue-700' 
                    : 'text-gray-700 hover:bg-gray-50'}`}
              >
                Columns
              </button>
              <button
                onClick={() => setActiveTab('statuses')}
                className={`w-full px-3 py-2 text-sm font-medium rounded-lg text-left
                  ${activeTab === 'statuses' 
                    ? 'bg-blue-50 text-blue-700' 
                    : 'text-gray-700 hover:bg-gray-50'}`}
              >
                Statuses
              </button>
              <button
                onClick={() => setActiveTab('tags')}
                className={`w-full px-3 py-2 text-sm font-medium rounded-lg text-left
                  ${activeTab === 'tags' 
                    ? 'bg-blue-50 text-blue-700' 
                    : 'text-gray-700 hover:bg-gray-50'}`}
              >
                Tags
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 p-6 overflow-y-auto">
              {activeTab === 'columns' && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-900">Add New Column</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Column Name
                        </label>
                        <input
                          type="text"
                          value={newColumnData.title}
                          onChange={(e) => setNewColumnData(prev => ({ 
                            ...prev, 
                            title: e.target.value 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                            focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder="Enter column name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Column Type
                        </label>
                        <select
                          value={newColumnData.type}
                          onChange={(e) => setNewColumnData(prev => ({ 
                            ...prev, 
                            type: e.target.value,
                            options: e.target.value === 'status' || e.target.value === 'tags' 
                              ? [] 
                              : prev.options
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                            focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        >
                          {columnTypes.map(type => (
                            <option key={type.id} value={type.id}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Options for status/tags type columns */}
                    {(newColumnData.type === 'status' || newColumnData.type === 'tags') && (
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium text-gray-900">Options</h4>
                        <div className="space-y-2">
                          {newColumnData.options.map((option, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <div
                                className="w-4 h-4 rounded"
                                style={{ backgroundColor: option.color }}
                              />
                              <span>{option.label}</span>
                              <button
                                onClick={() => {
                                  setNewColumnData(prev => ({
                                    ...prev,
                                    options: prev.options.filter((_, i) => i !== index)
                                  }));
                                }}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={newOption.label}
                            onChange={(e) => setNewOption(prev => ({ 
                              ...prev, 
                              label: e.target.value 
                            }))}
                            placeholder="Option name"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg 
                              focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          />
                          <input
                            type="color"
                            value={newOption.color}
                            onChange={(e) => setNewOption(prev => ({ 
                              ...prev, 
                              color: e.target.value 
                            }))}
                            className="w-10 h-10 rounded cursor-pointer"
                          />
                          <button
                            onClick={addOptionToColumn}
                            className="p-2 text-blue-600 hover:text-blue-700"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={addNewColumn}
                      className="px-4 py-2 text-sm font-medium text-white 
                        bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                      Add Column
                    </button>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-900">Current Columns</h4>
                    <div className="space-y-2">
                      {columns.map(column => (
                        <div key={column.id} className="flex items-center justify-between 
                          p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium">{column.title}</span>
                            <span className="text-xs text-gray-500">{column.type}</span>
                          </div>
                          {column.id !== 'name' && (
                            <button
                              onClick={() => {
                                setColumns(columns.filter(col => col.id !== column.id));
                              }}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'statuses' && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-900">Add New Status</h4>
                    <div className="flex items-center space-x-3">
                      <input
                        type="text"
                        value={newStatus.label}
                        onChange={(e) => setNewStatus(prev => ({ ...prev, label: e.target.value }))}
                        placeholder="Status name"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg 
                          focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      <input
                        type="color"
                        value={newStatus.color}
                        onChange={(e) => setNewStatus(prev => ({ ...prev, color: e.target.value }))}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <button
                        onClick={() => {
                          if (newStatus.label.trim()) {
                            const newStatusItem = {
                              id: newStatus.label.toLowerCase().replace(/\s+/g, '-'),
                              ...newStatus
                            };
                            setCustomStatuses(prev => [...prev, newStatusItem]);
                            setNewStatus({ label: '', color: '#00C875' });
                          }
                        }}
                        className="px-4 py-2 text-sm font-medium text-white 
                          bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        Add Status
                      </button>
                    </div>

                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Current Statuses</h4>
                      <div className="space-y-2">
                        {customStatuses.map((status, index) => (
                          <div key={status.id} 
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center space-x-3">
                              <div
                                className="w-4 h-4 rounded"
                                style={{ backgroundColor: status.color }}
                              />
                              <span className="text-sm font-medium">{status.label}</span>
                            </div>
                            <button
                              onClick={() => {
                                setCustomStatuses(prev => prev.filter((_, i) => i !== index));
                              }}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'tags' && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-900">Add New Tag</h4>
                    <div className="flex items-center space-x-3">
                      <input
                        type="text"
                        value={newTag.label}
                        onChange={(e) => setNewTag(prev => ({ ...prev, label: e.target.value }))}
                        placeholder="Tag name"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg 
                          focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      <input
                        type="color"
                        value={newTag.color}
                        onChange={(e) => setNewTag(prev => ({ ...prev, color: e.target.value }))}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <button
                        onClick={() => {
                          if (newTag.label.trim()) {
                            const newTagItem = {
                              id: newTag.label.toLowerCase().replace(/\s+/g, '-'),
                              ...newTag
                            };
                            setCustomTags(prev => [...prev, newTagItem]);
                            setNewTag({ label: '', color: '#579BFC' });
                          }
                        }}
                        className="px-4 py-2 text-sm font-medium text-white 
                          bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        Add Tag
                      </button>
                    </div>

                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Current Tags</h4>
                      <div className="space-y-2">
                        {customTags.map((tag, index) => (
                          <div key={tag.id} 
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center space-x-3">
                              <div
                                className="w-4 h-4 rounded"
                                style={{ backgroundColor: tag.color }}
                              />
                              <span className="text-sm font-medium">{tag.label}</span>
                            </div>
                            <button
                              onClick={() => {
                                setCustomTags(prev => prev.filter((_, i) => i !== index));
                              }}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    console.log('Items updated:', items);
  }, [items]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only handle dropdowns, not modals
      if (activeDropdown && 
          !event.target.closest('.dropdown-container') &&
          !event.target.closest('[role="dialog"]')) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeDropdown]);

  useEffect(() => {
    if (activeDropdown && buttonRefs.current[activeDropdown]) {
      const rect = buttonRefs.current[activeDropdown].getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  }, [activeDropdown]);

  return (
    <div className="min-h-screen bg-gray-50/50 p-6 space-y-6">
      {/* Header - Updated with better spacing and modern look */}
      <div className="flex items-center justify-between bg-white rounded-xl p-4 shadow-sm">
        <div className="flex items-center space-x-4">
          <Link
            to="/tracker"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Data Center Migration</h1>
            <p className="text-sm text-gray-500 mt-1">Track and manage your migration progress</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors">
            <Users className="w-4 h-4 mr-2" />
            Share
          </button>
          <button 
            onClick={() => setShowSettingsModal(true)}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <Settings2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Toolbar - Updated with modern design */}
      <div className="flex flex-wrap items-center gap-4 bg-white rounded-xl shadow-sm p-4">
        <div className="relative flex-1 min-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border-0 rounded-lg 
              focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
          />
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 
              bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import
          </button>
          <button
            onClick={() => setShowNewItemModal(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white 
              bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 
              hover:to-blue-800 transition-all shadow-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Item
          </button>
        </div>
      </div>

      {/* Table - Updated with modern styling */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        <TableHeader />
        <div className="divide-y divide-gray-100">
          {items.map(item => (
            <TableRow key={item.id} item={item} />
          ))}
        </div>
      </div>

      {/* Column Menu */}
      {showColumnMenu && (
        <div className="fixed inset-0 bg-black/20 flex items-start justify-center pt-20 z-[50]">
          <div className="bg-white rounded-lg shadow-xl w-80">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Edit Columns</h3>
                <button
                  onClick={() => setShowColumnMenu(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 space-y-2">
              {columns.map(column => (
                <div key={column.id} className="flex items-center justify-between">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={column.show}
                      onChange={() => {
                        setColumns(columns.map(col =>
                          col.id === column.id ? { ...col, show: !col.show } : col
                        ));
                      }}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">{column.title}</span>
                  </label>
                  {column.id !== 'name' && (
                    <button
                      onClick={() => {
                        setColumns(columns.filter(col => col.id !== column.id));
                      }}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showNewItemModal && <NewItemModal />}
      {showImportModal && <ImportModal />}
      {showSettingsModal && <SettingsModal />}
    </div>
  );
};

export default TrackerDetail; 